import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";

import type { PipelineSearch } from "..";
import { AccountDetailContent } from "./AccountDetailContent";
import { AccountQuickInfo } from "./AccountQuickInfo";

/**
 * Drawer adapter for a renewals customer (QuickBooks account). Mounted once
 * inside PipelinePage; visibility is fully derived from the route's search
 * params:
 *
 *   ?account=<qbId>              → quick view drawer
 *   ?account=<qbId>&expanded=1   → expanded drawer (full account view)
 *   no `account`                 → drawer closed
 *
 * The pipeline list stays mounted behind both modes, so closing returns to
 * the same scroll position and filters.
 */
export function CustomerDrawer() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/renewals/pipeline" });
  const qbId = search.account ?? null;
  const expanded = !!search.expanded;
  const open = qbId != null;

  // Re-navigate, touching only the drawer-owned params (`account`, `expanded`)
  // and preserving everything else — filters, sort, and the Setup-tab `setup`
  // flag. `account: null` closes the drawer. Without this, closing from the
  // Setup tab dropped `setup` and bounced the user to Pipeline.
  function setDrawer(next: { account: number | null; expanded?: boolean }) {
    navigate({
      to: "/renewals/pipeline",
      search: (prev: PipelineSearch) => {
        const { account: _a, expanded: _e, ...rest } = prev;
        if (next.account == null) return rest;
        return next.expanded
          ? { ...rest, account: next.account, expanded: true }
          : { ...rest, account: next.account };
      },
    });
  }

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        // ESC / backdrop in quick mode clears the account param. Expanded mode
        // ignores these (EntityDrawer already noops when expanded, but stay
        // defensive in case that changes).
        if (!v && !expanded) setDrawer({ account: null });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (qbId == null) return;
        setDrawer({ account: qbId, expanded: exp });
      }}
      onClose={() => {
        if (qbId == null) return;
        // The X always closes the drawer outright — including from expanded
        // mode, so you don't have to click it twice. Collapsing expanded →
        // quick view (without closing) is the header chevron's job.
        setDrawer({ account: null });
      }}
      title="Customer"
      subtitle={qbId != null ? `#${qbId}` : ""}
    >
      {qbId != null && (
        expanded
          ? <AccountDetailContent qbId={qbId} />
          : <AccountQuickInfo qbId={qbId} />
      )}
    </EntityDrawer>
  );
}
